import { FormEvent, useMemo, useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { hasBothRestrictions, isInviteDisabled } from '../user-management.format';
import {
    refreshUserManagementLists,
    useInviteColleague,
    useRestrictionFilters,
    useRoleNames,
} from '../user-management.queries';
import { filterOptionLabel, FormField, optionValues, PrefixRankedCombobox, restrictionFilterOption, roleFilterOption, SelectOption } from './FormField';

interface CreateUserDialogProps {
    open: boolean;
    onClose: () => void;
}

export function CreateUserDialog({ open, onClose }: CreateUserDialogProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const rolesQuery = useRoleNames();
    const filtersQuery = useRestrictionFilters();
    const invite = useInviteColleague();

    const [email, setEmail] = useState('');
    const [roles, setRoles] = useState<string[]>([]);
    const [restrictionFilter, setRestrictionFilter] = useState('');
    const [restrictionQuery, setRestrictionQuery] = useState('');

    const roleOptions = useMemo<SelectOption[]>(
        () => (rolesQuery.data ?? []).map((role) => ({ value: role, label: role })),
        [rolesQuery.data],
    );
    const filterOptions = useMemo<SelectOption[]>(() => {
        const loaded = (filtersQuery.data ?? []).filter((filter) => filter.name !== '');
        return [
            { value: '', label: t('MAKE_SELECTION') },
            ...loaded.map((filter) => ({ value: filter.name, label: filter.label })),
        ];
    }, [filtersQuery.data, t]);

    const selectedRoles = roleOptions.filter((option) => roles.includes(option.value));
    const selectedFilter = filterOptions.find((option) => option.value === restrictionFilter) ?? filterOptions[0];
    const inviteDisabled = isInviteDisabled(email, roles) || invite.isPending;

    const submit = () => {
        if (isInviteDisabled(email, roles) || invite.isPending) {
            return;
        }
        if (hasBothRestrictions(restrictionFilter, restrictionQuery)) {
            toast.error('USER_MANAGEMENT.ONLY_ONE_RESTRICTION.ERROR');
            return;
        }
        invite.mutate({
            invitedEmailAddress: email,
            roles,
            restrictionFilter,
            restrictionQuery,
        }, {
            onSuccess: () => {
                toast.success('USER_MANAGEMENT.INVITATION_SENT');
                refreshUserManagementLists();
                onClose();
            },
        });
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        submit();
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
            <DialogContent variant="small" className="modal-dialog">
                <DialogHeader>
                    <DialogHeaderDefault>{t('USER_MANAGEMENT.NEW_USER')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogDescription className="tw-sr-only">{t('USER_MANAGEMENT.NEW_USER')}</DialogDescription>
                <form onSubmit={onSubmit}>
                    <DialogBody className="tw-flex tw-max-h-[70vh] tw-flex-col tw-gap-6 tw-overflow-auto">
                        <FormField label={t('USER_MANAGEMENT.USER_NAME')} htmlFor="invitation-email">
                            <Input
                                id="invitation-email"
                                type="email"
                                value={email}
                                placeholder={t('USER_MANAGEMENT.EMAIL_ADDRESS')}
                                onChange={(event) => setEmail(event.target.value)}
                            />
                        </FormField>
                        <FormField label={t('USER_MANAGEMENT.ROLES')} htmlFor="invitation-roles">
                            <PrefixRankedCombobox
                                inputId="invitation-roles"
                                isMulti
                                options={roleOptions}
                                value={selectedRoles}
                                placeholder={t('GRID.DEFAULT_OPTION')}
                                filterOption={roleFilterOption}
                                onChange={(value) => setRoles(optionValues(value))}
                            />
                        </FormField>
                        <FormField label={t('USER_MANAGEMENT.RESTRICTION_FILTER')} htmlFor="invitation-restriction-filter">
                            <PrefixRankedCombobox
                                inputId="invitation-restriction-filter"
                                isClearable
                                options={filterOptions}
                                value={selectedFilter}
                                placeholder={t('GRID.DEFAULT_OPTION')}
                                filterOption={restrictionFilterOption}
                                formatOptionLabel={(option, meta) => filterOptionLabel(option as SelectOption, meta.context)}
                                onChange={(value) => setRestrictionFilter((value as SelectOption | null)?.value ?? '')}
                            />
                        </FormField>
                        <FormField label={t('USER_MANAGEMENT.RESTRICTION_QUERY')} htmlFor="invitation-restriction-query">
                            <Input
                                id="invitation-restriction-query"
                                name="restrictionQuery"
                                value={restrictionQuery}
                                onChange={(event) => setRestrictionQuery(event.target.value)}
                            />
                        </FormField>
                    </DialogBody>
                    <DialogFooter>
                        <Button id="invitation-submit" type="submit" disabled={inviteDisabled}>
                            {t('USER_MANAGEMENT.INVITE_USER_ACTION')}
                        </Button>
                        <Button variant="secondary" type="button" onClick={onClose}>
                            {t('CANCEL')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
